/**
 * Auth context: session state, login/register/logout, and role helpers.
 *
 * Platform invariant mirrored here (see CLAUDE.md): accredited-only UI is
 * gated on the SERVER-ASSIGNED role (`accredited-investor`), never on the
 * self-declared investorType from registration. Role upgrades happen only in
 * the proof-of-funds review flow, server-side. The frontend gate is a UX
 * convenience — the backend enforces the real check on every request.
 */
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import * as authApi from '../api/auth';
import * as registrationApi from '../api/registration';
import { readSession, writeSession, clearSession } from './session';
import { setSessionExpiredHandler } from './apiClient';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [session, setSession] = useState(() => readSession());

  useEffect(() => {
    setSessionExpiredHandler(() => setSession(null));
    return () => setSessionExpiredHandler(null);
  }, []);

  const adoptTokens = useCallback((data) => {
    const next = {
      // FND-52: the API's token field is `accessToken` (login/MFA/OAuth/refresh
      // all return it — src/shared/auth/identity.js). Reading `data.token`
      // stored undefined, so every call sent a malformed bearer, the refresh
      // retry failed the same way, and the app logged out instantly after
      // login. `data.token` kept as a fallback only.
      token: data.accessToken || data.token,
      refreshToken: data.refreshToken,
      expiresAt: data.expiresAt,
      user: data.user
    };
    writeSession(next);
    setSession(next);
    return next.user;
  }, []);

  /**
   * Password login. With MFA enabled the API returns
   * { mfaRequired: true, mfaToken } instead of a TokenPair — no session
   * exists yet; the caller must complete completeMfaLogin() with a TOTP or
   * recovery code within the token's 5-minute window.
   */
  const login = useCallback(async (credentials) => {
    const data = await authApi.login(credentials);
    if (data.mfaRequired) {
      return { mfaRequired: true, mfaToken: data.mfaToken };
    }
    return { user: adoptTokens(data) };
  }, [adoptTokens]);

  const completeMfaLogin = useCallback(async (mfaToken, codes) => {
    const data = await authApi.mfaChallenge(mfaToken, codes);
    return { user: adoptTokens(data), recoveryCodesRemaining: data.recoveryCodesRemaining };
  }, [adoptTokens]);

  /** Finish an OAuth flow: exchange the provider's code+state for a session. */
  const completeOauthLogin = useCallback(async (provider, params) => {
    const data = await authApi.oauthCallback(provider, params);
    return { user: adoptTokens(data) };
  }, [adoptTokens]);

  /** Patch fields on the cached user (e.g. mfaEnabled after enrollment). */
  const patchUser = useCallback((fields) => {
    setSession((current) => {
      if (!current) return current;
      const next = { ...current, user: { ...current.user, ...fields } };
      writeSession(next);
      return next;
    });
  }, []);

  const register = useCallback(async (payload) => {
    // Registration does NOT sign the user in — email verification comes first.
    return registrationApi.register(payload);
  }, []);

  const logout = useCallback(async () => {
    try {
      await authApi.logout();
    } catch {
      // Best-effort server-side revocation; the local session goes regardless.
    }
    clearSession();
    setSession(null);
  }, []);

  const value = useMemo(() => {
    const user = session && session.user;
    return {
      user: user || null,
      isAuthenticated: Boolean(session),
      isAccredited: Boolean(user && user.role === 'accredited-investor'),
      isReviewer: Boolean(user && ['admin', 'employee'].includes(user.role)),
      // Internal staff (src/services/forum/helpers.js STAFF_ROLES and
      // src/services/events/helpers.js STAFF_ROLES agree on this set).
      // isReviewer is intentionally narrower (admin|employee only — the
      // ReviewQueue's own gate) and stays untouched; isStaff is the gate for
      // events- and forum-moderation UI, which also includes sales.
      isStaff: Boolean(user && ['employee', 'sales', 'admin'].includes(user.role)),
      adoptTokens,
      login,
      completeMfaLogin,
      completeOauthLogin,
      patchUser,
      register,
      logout
    };
  }, [session, adoptTokens, login, completeMfaLogin, completeOauthLogin, patchUser, register, logout]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>');
  return ctx;
}
